import React from 'react';
import { storyContent } from '../data/mock';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const Story = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="story" className="relative py-32 lg:py-40 bg-[#020617] overflow-hidden">
      {/* Animated Background Texture */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1738082956257-06cde4444b1c?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/8 via-transparent to-[#020617] animate-gradient-pulse-soft" />
      <div className="absolute inset-0 bg-gradient-to-t from-transparent via-[#D4AF37]/4 to-transparent animate-gradient-wave-slow" />

      {/* Enhanced Floating Elements - More Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[18%] right-[22%] w-2.5 h-2.5 bg-[#D4AF37]/50 rounded-full animate-orbit-smooth-1 blur-sm" />
        <div className="absolute top-[55%] left-[28%] w-2 h-2 bg-white/40 rounded-full animate-orbit-smooth-2 blur-sm" />
        <div className="absolute bottom-[20%] right-[30%] w-1.5 h-1.5 bg-[#D4AF37]/60 rounded-full animate-orbit-smooth-3 blur-sm" />
        <div className="absolute top-[42%] left-[20%] w-2 h-2 bg-white/50 rounded-full animate-orbit-smooth-4 blur-sm" />
        <div className="absolute bottom-[35%] right-[18%] w-1 h-1 bg-[#D4AF37]/45 rounded-full animate-orbit-smooth-5 blur-sm" />
        <div className="absolute top-[68%] left-[45%] w-1.5 h-1.5 bg-white/35 rounded-full animate-orbit-smooth-6 blur-sm" />
        <div className="absolute top-[30%] right-[45%] w-1 h-1 bg-[#D4AF37]/55 rounded-full animate-orbit-smooth-7 blur-sm" />
        <div className="absolute bottom-[48%] left-[38%] w-1.5 h-1.5 bg-white/45 rounded-full animate-orbit-smooth-8 blur-sm" />
      </div>

      {/* Smooth Light Beams */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-[22%] w-[1px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/18 to-transparent animate-beam-drift-smooth-1" />
        <div className="absolute top-0 right-[28%] w-[1px] h-full bg-gradient-to-b from-transparent via-white/12 to-transparent animate-beam-drift-smooth-2" />
        <div className="absolute top-0 left-[60%] w-[1px] h-full bg-gradient-to-b from-transparent via-[#D4AF37]/15 to-transparent animate-beam-drift-smooth-3" />
      </div>

      {/* Pulsing Ambient Orbs */}
      <div className="absolute top-1/5 right-1/5 w-72 h-72 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse-ambient-soft-1" />
      <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-[#D4AF37]/8 rounded-full blur-3xl animate-pulse-ambient-soft-2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Section Header */}
        <div className="mb-20 animate-section-reveal-smooth">
          <div className="flex items-center gap-6 mb-8">
            <div className="w-16 h-[1px] bg-[#D4AF37] animate-line-draw-smooth" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-light animate-text-glow-pulse-soft">{storyContent.subtitle}</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-8 tracking-tight max-w-4xl animate-title-appear-smooth">
            {storyContent.title}
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-3xl leading-relaxed font-light animate-fade-up-smooth animation-delay-400">
            {storyContent.description}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          {/* Institutional Background */}
          <div className="animate-card-slide-in-smooth">
            <div className="bg-[#0f172a]/70 backdrop-blur-md border border-white/10 hover:border-[#D4AF37]/60 p-10 h-full transition-all duration-1000 hover:scale-105 hover:shadow-2xl hover:shadow-[#D4AF37]/25 group relative overflow-hidden">
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 via-[#D4AF37]/8 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500" />
              
              {/* Pulsing Corner Light */}
              <div className="absolute top-0 right-0 w-24 h-24 bg-[#D4AF37]/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-1000" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-[1px] bg-[#D4AF37]" />
                  <h3 className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase font-light">Institutional Heritage</h3>
                </div>
                <p className="text-white text-base font-light leading-relaxed mb-6">{storyContent.teamBackground}</p>
                
                {/* Institutions in bullet columns */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {storyContent.institutions.map((institution, idx) => (
                    <div key={idx} className="flex items-start gap-2 group/item animate-list-pop-smooth" style={{ animationDelay: `${idx * 60}ms` }}>
                      <div className="w-1 h-1 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0 group-hover/item:scale-125 transition-transform duration-300" />
                      <span className="text-[#94A3B8] group-hover/item:text-white transition-colors duration-300 text-sm font-light leading-relaxed">{institution}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Expertise Grid */}
          <div className="animate-card-slide-in-smooth animation-delay-200">
            <div className="bg-[#0f172a]/70 backdrop-blur-md border border-white/10 hover:border-[#D4AF37]/40 p-10 h-full transition-all duration-1000 hover:shadow-xl group relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500" />
              
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-[1px] bg-[#D4AF37]" />
                  <h3 className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase font-light">Core Capabilities</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {storyContent.expertise.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 group/item animate-list-pop-smooth" style={{ animationDelay: `${(idx + 4) * 80}ms` }}>
                      <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full group-hover/item:scale-125 transition-transform duration-500" />
                      <span className="text-[#94A3B8] group-hover/item:text-white transition-colors duration-500 text-sm font-light">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Approach Section */}
        <div className="mb-20 animate-fade-scale-smooth animation-delay-600">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-white text-xl md:text-2xl font-light leading-relaxed mb-12 italic">
              "{storyContent.approach}"
            </p>
            <div className="flex items-center justify-center gap-6">
              <div className="w-24 h-[1px] bg-[#D4AF37] animate-line-expand-soft" />
              <div className="w-2 h-2 bg-[#D4AF37] rounded-full animate-pulse-soft" />
              <div className="w-24 h-[1px] bg-[#D4AF37] animate-line-expand-soft" />
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center animate-fade-scale-smooth animation-delay-800">
          <Button
            onClick={scrollToContact}
            className="group relative bg-[#D4AF37] hover:bg-white text-[#020617] px-12 py-7 font-serif text-lg tracking-wide transition-all duration-1000 hover:scale-110 hover:shadow-2xl hover:shadow-[#D4AF37]/50 overflow-hidden"
          >
            <span className="relative z-10 flex items-center">
              Begin Your Journey With Us
              <ArrowRight className="ml-3 w-5 h-5 transition-transform duration-1000 group-hover:translate-x-3" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1500" />
          </Button>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes gradient-pulse-soft {
          0%, 100% { opacity: 0.08; }
          50% { opacity: 0.15; }
        }

        @keyframes gradient-wave-slow {
          0%, 100% { transform: translateY(0); opacity: 0.04; }
          50% { transform: translateY(-30px); opacity: 0.10; }
        }

        @keyframes orbit-smooth-1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.5; }
          25% { transform: translate(70px, -90px) rotate(90deg); opacity: 0.7; }
          50% { transform: translate(90px, 30px) rotate(180deg); opacity: 0.85; }
          75% { transform: translate(30px, 80px) rotate(270deg); opacity: 0.6; }
        }

        @keyframes orbit-smooth-2 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
          50% { transform: translate(-80px, 100px) scale(1.5); opacity: 0.75; }
        }

        @keyframes orbit-smooth-3 {
          0%, 100% { transform: translate(0, 0); opacity: 0.6; }
          50% { transform: translate(60px, -70px); opacity: 0.85; }
        }

        @keyframes orbit-smooth-4 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.5; }
          50% { transform: translate(-70px, -80px) rotate(200deg); opacity: 0.8; }
        }

        @keyframes orbit-smooth-5 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.45; }
          50% { transform: translate(50px, 60px) scale(1.3); opacity: 0.75; }
        }

        @keyframes orbit-smooth-6 {
          0%, 100% { transform: translate(0, 0); opacity: 0.35; }
          50% { transform: translate(-60px, -50px); opacity: 0.65; }
        }

        @keyframes orbit-smooth-7 {
          0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.55; }
          50% { transform: translate(75px, 85px) rotate(180deg); opacity: 0.8; }
        }

        @keyframes orbit-smooth-8 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.45; }
          50% { transform: translate(-65px, 75px) scale(1.4); opacity: 0.7; }
        }

        @keyframes beam-drift-smooth-1 {
          0%, 100% { opacity: 0; transform: translateY(-100%); }
          50% { opacity: 0.18; transform: translateY(100%); }
        }

        @keyframes beam-drift-smooth-2 {
          0%, 100% { opacity: 0; transform: translateY(100%); }
          50% { opacity: 0.12; transform: translateY(-100%); }
        }

        @keyframes beam-drift-smooth-3 {
          0%, 100% { opacity: 0; transform: translateY(-80%); }
          50% { opacity: 0.15; transform: translateY(120%); }
        }

        @keyframes pulse-ambient-soft-1 {
          0%, 100% { opacity: 0.10; transform: scale(1); }
          50% { opacity: 0.18; transform: scale(1.35); }
        }

        @keyframes pulse-ambient-soft-2 {
          0%, 100% { opacity: 0.08; transform: scale(1); }
          50% { opacity: 0.14; transform: scale(1.4); }
        }

        @keyframes section-reveal-smooth {
          0% { opacity: 0; transform: translateY(50px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes line-draw-smooth {
          0% { width: 0; opacity: 0; }
          100% { width: 4rem; opacity: 1; }
        }

        @keyframes text-glow-pulse-soft {
          0%, 100% { text-shadow: 0 0 12px rgba(212, 175, 55, 0.35); }
          50% { text-shadow: 0 0 24px rgba(212, 175, 55, 0.55); }
        }

        @keyframes title-appear-smooth {
          0% { opacity: 0; transform: translateX(-50px); filter: blur(10px); }
          100% { opacity: 1; transform: translateX(0); filter: blur(0); }
        }

        @keyframes fade-up-smooth {
          0% { opacity: 0; transform: translateY(35px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes card-slide-in-smooth {
          0% { opacity: 0; transform: translateX(-50px); }
          100% { opacity: 1; transform: translateX(0); }
        }

        @keyframes list-pop-smooth {
          0% { opacity: 0; transform: translateX(-12px) scale(0.95); }
          100% { opacity: 1; transform: translateX(0) scale(1); }
        }

        @keyframes fade-scale-smooth {
          0% { opacity: 0; transform: scale(0.95); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes line-expand-soft {
          0% { width: 0; }
          100% { width: 6rem; }
        }

        @keyframes pulse-soft {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.3); }
        }

        .animate-gradient-pulse-soft { animation: gradient-pulse-soft 12s ease-in-out infinite; }
        .animate-gradient-wave-slow { animation: gradient-wave-slow 18s ease-in-out infinite; }
        .animate-orbit-smooth-1 { animation: orbit-smooth-1 28s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-orbit-smooth-2 { animation: orbit-smooth-2 24s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-orbit-smooth-3 { animation: orbit-smooth-3 26s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-orbit-smooth-4 { animation: orbit-smooth-4 28s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-orbit-smooth-5 { animation: orbit-smooth-5 25s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-orbit-smooth-6 { animation: orbit-smooth-6 27s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-orbit-smooth-7 { animation: orbit-smooth-7 29s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-orbit-smooth-8 { animation: orbit-smooth-8 26s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite; }
        .animate-beam-drift-smooth-1 { animation: beam-drift-smooth-1 20s ease-in-out infinite; }
        .animate-beam-drift-smooth-2 { animation: beam-drift-smooth-2 22s ease-in-out infinite; }
        .animate-beam-drift-smooth-3 { animation: beam-drift-smooth-3 24s ease-in-out infinite; }
        .animate-pulse-ambient-soft-1 { animation: pulse-ambient-soft-1 12s ease-in-out infinite; }
        .animate-pulse-ambient-soft-2 { animation: pulse-ambient-soft-2 14s ease-in-out infinite; }
        .animate-section-reveal-smooth { animation: section-reveal-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-line-draw-smooth { animation: line-draw-smooth 1s ease-out forwards; }
        .animate-text-glow-pulse-soft { animation: text-glow-pulse-soft 4s ease-in-out infinite; }
        .animate-title-appear-smooth { animation: title-appear-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fade-up-smooth { animation: fade-up-smooth 1.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-card-slide-in-smooth { animation: card-slide-in-smooth 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-list-pop-smooth { animation: list-pop-smooth 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards; opacity: 0; }
        .animate-fade-scale-smooth { animation: fade-scale-smooth 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
        .animate-line-expand-soft { animation: line-expand-soft 1.5s ease-out forwards; }
        .animate-pulse-soft { animation: pulse-soft 3s ease-in-out infinite; }

        .animation-delay-200 { animation-delay: 0.2s; }
        .animation-delay-400 { animation-delay: 0.4s; }
        .animation-delay-600 { animation-delay: 0.6s; }
        .animation-delay-800 { animation-delay: 0.8s; }
      `}</style>
    </section>
  );
};

export default Story;
